/**
 * Auth Store - Pinia wrapper for vue3-oidc
 * Provides a convenient reactive interface for authentication state.
 */
import { defineStore } from 'pinia'
import { computed } from 'vue'
import { useOidcStore, useAuth } from 'vue3-oidc'

export const useAuthStore = defineStore('auth', () => {
  const { state } = useOidcStore()
  const { signinRedirect, signoutRedirect, autoAuthenticate } = useAuth()

  // Computed state
  const user = computed(() => state.value?.user ?? null)
  const isAuthenticated = computed(() => !!state.value?.user)
  const token = computed(() => state.value?.token ?? null)

  // User profile helpers
  const userName = computed(() => {
    const profile = user.value?.profile
    return profile?.name ?? profile?.preferred_username ?? 'User'
  })

  const userEmail = computed(() => {
    return user.value?.profile?.email ?? ''
  })

  // Role extraction - supports both Keycloak and Entra formats
  const userRoles = computed<string[]>(() => {
    const profile = user.value?.profile as Record<string, unknown> | undefined
    if (!profile) return []

    // Keycloak format: realm_access.roles
    const realmAccess = profile.realm_access as { roles?: string[] } | undefined
    if (realmAccess?.roles && Array.isArray(realmAccess.roles)) {
      return realmAccess.roles
    }

    // Microsoft Entra format: roles (flat array)
    const entraRoles = profile.roles
    if (Array.isArray(entraRoles)) {
      return entraRoles as string[]
    }

    return []
  })

  // Role checking
  function hasRole(roles: string | string[]): boolean {
    const roleArray = Array.isArray(roles) ? roles : [roles]
    return roleArray.some(role => userRoles.value.includes(role))
  }

  // Auth actions
  async function login() {
    await signinRedirect()
  }

  async function logout() {
    await signoutRedirect()
  }

  async function checkAuth() {
    await autoAuthenticate()
  }

  return {
    // State
    user,
    isAuthenticated,
    token,
    userName,
    userEmail,
    userRoles,

    // Methods
    hasRole,
    login,
    logout,
    checkAuth,
  }
})